import { ButtonType } from '@ardoq/button';
import { ButtonWithDropdown, DropdownOptionType } from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { IconName } from '@ardoq/icons';
import { ArdoqId } from '@ardoq/api-types';
import { EnhancedScopeData } from '@ardoq/data-model';
import { isTruthy } from '@ardoq/common-helpers';
import { referenceAttributesOperations } from '@ardoq/core';

export type ReferenceFilterLabels = {
  filterButton?: string;
  directionHeader?: string;
  directionAll?: string;
  directionOutgoing?: string;
  directionIncoming?: string;
  typeHeader?: string;
  parentChildReferenceName?: string;
};

const DEFAULT_PARENT_CHILD_REFERENCE_NAME = 'Is parent of';

export const getAllReferences = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  return scopeData.references.filter(
    ref => ref.target === componentId || ref.source === componentId
  );
};

const updateFilter = (
  newFilter: string,
  existingFilters: Set<string>
): Set<string> => {
  if (existingFilters.has(newFilter)) {
    existingFilters.delete(newFilter);
    return existingFilters;
  }
  return existingFilters.add(newFilter);
};

export const getReferenceFilterOptions = (
  scopeData: EnhancedScopeData,
  filters: ReferenceFilterOptions,
  setFilters: (filters: ReferenceFilterOptions) => void,
  labels: ReferenceFilterLabels = {}
) => {
  const {
    directionHeader = 'Direction',
    directionAll = 'All',
    directionOutgoing = 'Outgoing',
    directionIncoming = 'Incoming',
    typeHeader = 'Type',
    parentChildReferenceName = DEFAULT_PARENT_CHILD_REFERENCE_NAME,
  } = labels;
  const referenceGroupedByTypes = scopeData.graphData.referenceTypes;
  const parentChildReferences = scopeData.references.filter(
    referenceAttributesOperations.isSyntheticParentChildReference
  );
  if (!referenceGroupedByTypes.length && !parentChildReferences.length)
    return [];

  const referenceTypeOptions = referenceGroupedByTypes.map(typeGroup => {
    return {
      label: typeGroup.name,
      name: typeGroup.name,
      truncateLabel: true,
      type: DropdownOptionType.OPTION,
      isSelected: filters.types.has(typeGroup.name),
      isActive: filters.types.has(typeGroup.name),
      onClick: () =>
        setFilters({
          ...filters,
          types: updateFilter(typeGroup.name, filters.types),
        }),
    };
  });

  const parentTypeOption = parentChildReferences.length
    ? {
        label: parentChildReferenceName,
        name: parentChildReferenceName,
        truncateLabel: true,
        type: DropdownOptionType.OPTION,
        isSelected: filters.types.has(parentChildReferenceName),
        isActive: filters.types.has(parentChildReferenceName),
        onClick: () =>
          setFilters({
            ...filters,
            types: updateFilter(parentChildReferenceName, filters.types),
          }),
      }
    : null;

  const directionOptions = [
    {
      label: directionHeader,
      type: DropdownOptionType.HEADER,
    },
    {
      label: directionAll,
      name: 'all',
      type: DropdownOptionType.OPTION,
      isSelected: filters.direction === 'all',
      isActive: filters.direction === 'all',
      onClick: () =>
        setFilters({
          ...filters,
          direction: 'all',
        }),
    },
    {
      label: directionOutgoing,
      name: 'outgoing',
      type: DropdownOptionType.OPTION,
      isSelected: filters.direction === 'outgoing',
      isActive: filters.direction === 'outgoing',
      onClick: () =>
        setFilters({
          ...filters,
          direction: 'outgoing',
        }),
    },
    {
      label: directionIncoming,
      name: 'incoming',
      type: DropdownOptionType.OPTION,
      isSelected: filters.direction === 'incoming',
      isActive: filters.direction === 'incoming',
      onClick: () =>
        setFilters({
          ...filters,
          direction: 'incoming',
        }),
    },
  ];

  const referenceOptions = [...referenceTypeOptions, parentTypeOption].filter(
    isTruthy
  );

  const typeOptions = [
    {
      label: typeHeader,
      type: DropdownOptionType.HEADER,
    },
    ...referenceOptions,
  ];

  return [
    ...directionOptions,
    { type: DropdownOptionType.DIVIDER },
    ...typeOptions,
  ];
};

export type ReferenceFilterOptions = {
  types: Set<string>;
  direction: string;
};

type ReferenceFilterProps = {
  filters: ReferenceFilterOptions;
  scopeData: EnhancedScopeData;
  setFilters: (filters: ReferenceFilterOptions) => void;
  labels?: ReferenceFilterLabels;
};

const ReferenceFilter = ({
  filters,
  scopeData,
  setFilters,
  labels,
}: ReferenceFilterProps) => {
  const filterButtonLabel = labels?.filterButton ?? 'Filter';
  const filterOptions = getReferenceFilterOptions(
    scopeData,
    filters,
    setFilters,
    labels
  );
  return (
    <div>
      <ButtonWithDropdown
        dataTestId="filter-button"
        options={filterOptions}
        iconName={IconName.FILTER_LIST}
        minDropdownSize={DropdownSize.S}
        maxDropdownSize={DropdownSize.M}
        isDisabled={!filterOptions.length}
        buttonType={ButtonType.GHOST}
      >
        {filterButtonLabel}
      </ButtonWithDropdown>
    </div>
  );
};

export { ReferenceFilter };
